import type { ReactNode } from 'react';

import { Icon } from '../components/Icon.js';
import styles from './WidgetHeader.module.css';

interface WidgetHeaderProps {
  /** "Chats" over the list, the counterpart's name over a conversation. */
  title: string;
  /** Back to the list. Absent on the list itself, which has nowhere to go. */
  onBack?: () => void;
  /**
   * Open /chat. Drawn over the list, and over a conversation only when that
   * conversation has no actions of its own — those leave for the page too, and
   * name what to open when they get there, so an expand beside them is a third
   * button for a trip two already make. Absent, there is no way out at all.
   */
  onExpand?: () => void;
  /** Open /chat's Info section, drawn beside the expand. When it is offered is
   *  the panel's call, not this row's — see `overList` in `WidgetPanel`. */
  onInfo?: () => void;
  /** Whether anything in Info is unread, which is all the dot claims. */
  infoUnread?: boolean;
  /** Always required: on mobile this is the only way out. See `.close`. */
  onClose: () => void;
  /**
   * What can be done to what the header names, left of the chrome — the hiring
   * group over a conversation, nothing over the list.
   *
   * Passed in rather than built here because the acts need the conversation (its
   * stage, whether it is still open), and that lives in `WidgetThread`. Same
   * division as /chat, where `ChatView` owns `useConversation` and hands
   * `ConversationHeader` the actions it drew from it.
   */
  actions?: ReactNode;
}

/**
 * The bar over whichever of the widget's views is showing.
 *
 * One component for all of them so the chrome can't drift: back, title and close
 * land in the same place at the same size either way, and a second copy of the
 * row is how two views stop agreeing about that. What differs is only what each
 * has to offer — the list expands, a conversation carries its own actions and
 * leaves through them.
 */
export function WidgetHeader({
  title,
  onBack,
  onExpand,
  onInfo,
  infoUnread,
  onClose,
  actions,
}: WidgetHeaderProps) {
  return (
    <header className={styles.header} data-drag-handle>
      {onBack && (
        <button className={styles.iconButton} onClick={onBack} aria-label="Back to chats">
          <Icon name="back" size={17} />
        </button>
      )}
      <span className={styles.title}>{title}</span>
      {actions}
      {onInfo && (
        <button
          className={styles.iconButton}
          onClick={onInfo}
          // Two labels because the dot is the whole point of the button being
          // here at all: a red circle says nothing to a screen reader, and a
          // single "Info messages" would leave the one fact it carries readable
          // by sighted employers only.
          aria-label={infoUnread ? 'Info messages, unread' : 'Info messages'}
          title="Info messages"
        >
          {/* The rail's glyph for the same section, so the two surfaces name it
              with one icon. See `ChatRail`. */}
          <Icon name="megaphone" size={16} />
          {infoUnread && <span className={styles.dot} />}
        </button>
      )}
      {onExpand && (
        <button
          className={styles.iconButton}
          onClick={onExpand}
          aria-label="Open in full page"
          title="Open in full page"
        >
          <Icon name="expand" size={16} />
        </button>
      )}
      {/* Mobile only — the FAB is the close everywhere else. See `.close`. */}
      <button className={styles.close} onClick={onClose} aria-label="Close chat">
        <Icon name="close" size={17} />
      </button>
    </header>
  );
}
